/**
 * @fileoverview Global fiscal-basis state — WHICH fiscal year the charts mean.
 * @module hooks/useFiscalBasis
 *
 * Called ONCE in MainApp; the result goes to <FiscalBasisProvider>. Every view
 * reads it via `useFiscalBasisContext()`. Same shape of contract as
 * useTimeFilter / TimeFilterContext — one source of truth, never prop-drilled.
 *
 * THE BASIS IS THE VIEWER'S CALENDAR, NOT THE VIEWED VENDOR'S.
 * Charting Cisco's California spend on the Palo Alto Networks fiscal year is
 * the point of the feature: Ryan sells against his own quota year. The basis
 * is therefore never derived from the `vendor` route param.
 *
 * REGISTRY, NOT AN ENUM. The list of calendars comes from the backend
 * (`GET /api/fiscal-calendars`), with the two @techstack/core bootstrap rows
 * used for first paint and whenever the fetch fails. When a `fiscal_calendars`
 * table + cron lands, every new vendor calendar appears in the toggle with no
 * change here — the toggle maps over whatever the registry returns.
 */

import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  FISCAL_CALENDARS,
  DEFAULT_FISCAL_CALENDAR_KEY,
  getFiscalCalendar,
} from '@techstack/core';
import apiClient from '../lib/apiClient';

/** localStorage key. Survives reload; per-browser until it moves to settings. */
export const FISCAL_BASIS_STORAGE_KEY = 'tekstak.fiscalBasis';

function readStoredBasis() {
  try {
    return window.localStorage.getItem(FISCAL_BASIS_STORAGE_KEY) || null;
  } catch {
    // Private mode / disabled storage — fall back to the default, don't crash.
    return null;
  }
}

function writeStoredBasis(key) {
  try {
    window.localStorage.setItem(FISCAL_BASIS_STORAGE_KEY, key);
  } catch {
    /* non-fatal */
  }
}

/** Map an API row (snake_case) onto the registry shape the helpers expect. */
function fromApiRow(row) {
  return {
    key: row.key,
    label: row.label,
    shortLabel: row.short_label,
    fyStartMonth: row.fy_start_month,
    fyStartDay: row.fy_start_day,
    labelConvention: row.label_convention,
    source: row.source,
    updatedAt: row.updated_at,
  };
}

/**
 * @returns {{
 *   basis: string,
 *   setBasis: (key: string) => void,
 *   calendar: object,
 *   calendars: object[],
 *   isDefaultBasis: boolean,
 * }}
 */
export function useFiscalBasis() {
  const [basis, setBasisRaw] = useState(
    () => readStoredBasis() || DEFAULT_FISCAL_CALENDAR_KEY,
  );

  // Reference data, effectively immutable — cache it hard and never refetch on
  // focus. The bootstrap rows mean the toggle renders correctly before this
  // resolves and stays correct if the backend is unreachable.
  const { data: calendars = FISCAL_CALENDARS } = useQuery({
    queryKey: ['fiscal-calendars'],
    queryFn: async () => {
      const res = await apiClient.get('/api/fiscal-calendars');
      const rows = Array.isArray(res?.calendars) ? res.calendars : [];
      return rows.length ? rows.map(fromApiRow) : FISCAL_CALENDARS;
    },
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    placeholderData: FISCAL_CALENDARS,
  });

  const setBasis = useCallback((key) => {
    // Resolve through the registry so an unknown key can never be stored.
    const resolved = getFiscalCalendar(key, calendars).key;
    setBasisRaw(resolved);
    writeStoredBasis(resolved);
  }, [calendars]);

  // A stored basis whose calendar has since disappeared from the registry
  // silently degrades to the default rather than rendering an empty toggle.
  const calendar = useMemo(
    () => getFiscalCalendar(basis, calendars),
    [basis, calendars],
  );

  return {
    basis: calendar.key,
    setBasis,
    calendar,
    calendars,
    isDefaultBasis: calendar.key === DEFAULT_FISCAL_CALENDAR_KEY,
  };
}

export default useFiscalBasis;
